import { getFloatField } from '@lender/utils';
import _ from 'lodash';
import { compose } from 'redux';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { LoanType } from 'constants/loanApplication';
import { MAX_PERCENTAGE_VALUE } from 'constants/number';
import {
  amortizationTypeOptions,
  assumableDetailsOptions,
  loanProgramAmortizationTypeOptions,
  minimumInitialDrawTypeOptions,
  prepaymentPenaltyFeatureOptions,
  programLoanTypeOptions,
  rateTypeOptions,
} from 'constants/options';
import { LoanProgramFieldName, MinimumInitialDrawType } from 'constants/programs';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { getMustBeGreaterThanZeroValidationMessage } from 'utils/validation';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';

import { BALLOON_TERM_MIN_MONTHS, ratePercentInputProps } from './LoanInfo.constants';

const getPositiveRateSchema = label => () =>
  getFloatField({ isRequired: false }).moreThan(
    0,
    getMustBeGreaterThanZeroValidationMessage({ fieldName: label }),
  );

const loanProgramFields = {
  [LoanProgramFieldName.PROGRAM_CODE]: {
    fieldId: LoanProgramFieldName.PROGRAM_CODE,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.PROGRAM_CODE,
    inputProps: {
      label: 'Program Code',
      disabled: true,
    },
  },
  [LoanProgramFieldName.PROGRAM_LOAN_TYPE]: {
    fieldId: LoanProgramFieldName.PROGRAM_LOAN_TYPE,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.PROGRAM_LOAN_TYPE,
    inputProps: {
      options: programLoanTypeOptions,
      label: 'Loan Type',
      required: false,
    },
  },
  [LoanProgramFieldName.LOAN_TERM]: {
    fieldId: LoanProgramFieldName.LOAN_TERM,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.LOAN_TERM,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Loan Term (months)',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_LOAN_TERM_INPUT,
    },
  },
  [LoanProgramFieldName.AMORT_TERM]: {
    fieldId: LoanProgramFieldName.AMORT_TERM,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.AMORT_TERM,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Amortization Term (months)',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_AMORT_TERM_INPUT,
    },
  },
  [LoanProgramFieldName.AMORTIZATION_TYPE]: {
    fieldId: LoanProgramFieldName.AMORTIZATION_TYPE,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.AMORTIZATION_TYPE,
    inputProps: {
      options: loanProgramAmortizationTypeOptions,
      label: 'Amortization Type',
      required: false,
    },
  },
  [LoanProgramFieldName.INTEREST_ONLY_PERIOD]: {
    fieldId: LoanProgramFieldName.INTEREST_ONLY_PERIOD,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.INTEREST_ONLY_PERIOD,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Interest Only Period (months)',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_INTEREST_ONLY_PERIOD_INPUT,
    },
  },
  [LoanProgramFieldName.BALLOON_TERM_MONTHS]: {
    fieldId: LoanProgramFieldName.BALLOON_TERM_MONTHS,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.BALLOON_TERM_MONTHS,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Balloon Payment Term (months)',
      required: false,
      decimalScale: 0,
      minValue: BALLOON_TERM_MIN_MONTHS,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_BALLOON_TERM_MONTHS_INPUT,
    },
  },
  [LoanProgramFieldName.ARM_INITIAL_CAP]: {
    fieldId: LoanProgramFieldName.ARM_INITIAL_CAP,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ARM_INITIAL_CAP,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Initial Cap',
      required: false,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_ARM_INITIAL_CAP_INPUT,
    },
  },
  [LoanProgramFieldName.ARM_ADJUSTMENT_CAP]: {
    fieldId: LoanProgramFieldName.ARM_ADJUSTMENT_CAP,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ARM_ADJUSTMENT_CAP,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Adjustment Cap',
      required: false,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_ARM_ADJUSTMENT_CAP_INPUT,
    },
  },
  [LoanProgramFieldName.ARM_SUBSEQUENT_CAP]: {
    fieldId: LoanProgramFieldName.ARM_SUBSEQUENT_CAP,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ARM_SUBSEQUENT_CAP,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Subsequent Cap',
      required: false,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_ARM_SUBSEQUENT_CAP_INPUT,
    },
  },
  [LoanProgramFieldName.ARM_RATE_ADJ_PERIOD]: {
    fieldId: LoanProgramFieldName.ARM_RATE_ADJ_PERIOD,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ARM_RATE_ADJ_PERIOD,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Rate Adjustment Period',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_ARM_RATE_ADJ_PERIOD_INPUT,
    },
  },
  [LoanProgramFieldName.ARM_SUB_RATE_ADJ_PERIOD]: {
    fieldId: LoanProgramFieldName.ARM_SUB_RATE_ADJ_PERIOD,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ARM_SUB_RATE_ADJ_PERIOD,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Subsequent Rate Adj Period',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_ARM_SUB_RATE_ADJ_PERIOD_INPUT,
    },
  },
  [LoanProgramFieldName.ARM_FLOOR_RATE]: {
    fieldId: LoanProgramFieldName.ARM_FLOOR_RATE,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ARM_FLOOR_RATE,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Floor / Minimum Rate',
      required: false,
      customSchema: getPositiveRateSchema('Floor / Minimum Rate'),
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_ARM_FLOOR_RATE_INPUT,
    },
  },
  [LoanProgramFieldName.ARM_CEILING_RATE]: {
    fieldId: LoanProgramFieldName.ARM_CEILING_RATE,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ARM_CEILING_RATE,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Ceiling Rate',
      required: false,
      customSchema: getPositiveRateSchema('Ceiling Rate'),
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_ARM_CEILING_RATE_INPUT,
    },
  },
  [LoanProgramFieldName.ARM_ADJ_CHANGE_INDEX_RATE]: {
    fieldId: LoanProgramFieldName.ARM_ADJ_CHANGE_INDEX_RATE,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ARM_ADJ_CHANGE_INDEX_RATE,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Adj Change Index Rate',
      required: false,
      customSchema: getPositiveRateSchema('Adj Change Index Rate'),
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_ARM_ADJ_CHANGE_INDEX_RATE_INPUT,
    },
  },
  [LoanProgramFieldName.INDEX_TYPE_ID]: {
    fieldId: LoanProgramFieldName.INDEX_TYPE_ID,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.INDEX_TYPE_ID,
    inputProps: {
      label: 'Index Type',
      required: false,
    },
  },
  [LoanProgramFieldName.INDEX_MARGIN]: {
    fieldId: LoanProgramFieldName.INDEX_MARGIN,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.INDEX_MARGIN,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Index Margin',
      required: false,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_INDEX_MARGIN_INPUT,
    },
  },
  [LoanProgramFieldName.INDEX_RATE]: {
    fieldId: LoanProgramFieldName.INDEX_RATE,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.INDEX_RATE,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Index Rate',
      required: false,
      maxValue: MAX_PERCENTAGE_VALUE,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_INDEX_RATE_INPUT,
    },
  },
  [LoanProgramFieldName.INDEX_PERIOD_DAYS]: {
    fieldId: LoanProgramFieldName.INDEX_PERIOD_DAYS,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.INDEX_PERIOD_DAYS,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Index Period (days)',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_INDEX_PERIOD_DAYS_INPUT,
    },
  },
  [LoanProgramFieldName.PREPAY_FEATURE_TYPE]: {
    fieldId: LoanProgramFieldName.PREPAY_FEATURE_TYPE,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.PREPAY_FEATURE_TYPE,
    inputProps: {
      options: prepaymentPenaltyFeatureOptions,
      label: 'Prepayment Feature Type',
      required: false,
    },
  },
  [LoanProgramFieldName.PREPAY_MONTHS_IN_AFFECT]: {
    fieldId: LoanProgramFieldName.PREPAY_MONTHS_IN_AFFECT,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.PREPAY_MONTHS_IN_AFFECT,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Months In Affect',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_PREPAY_MONTHS_IN_AFFECT_INPUT,
    },
  },
  [LoanProgramFieldName.PREPAY_CHARGE_TERM_MONTHS]: {
    fieldId: LoanProgramFieldName.PREPAY_CHARGE_TERM_MONTHS,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.PREPAY_CHARGE_TERM_MONTHS,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Charge Term (months)',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_PREPAY_CHARGE_TERM_MONTHS_INPUT,
    },
  },
  [LoanProgramFieldName.PREPAY_CHARGE_RATE]: {
    fieldId: LoanProgramFieldName.PREPAY_CHARGE_RATE,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.PREPAY_CHARGE_RATE,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Charge Rate',
      required: false,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_PREPAY_CHARGE_RATE_INPUT,
    },
  },
  [LoanProgramFieldName.PREPAY_MAX_AMOUNT]: {
    fieldId: LoanProgramFieldName.PREPAY_MAX_AMOUNT,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.PREPAY_MAX_AMOUNT,
    inputProps: {
      type: FieldInputType.CURRENCY,
      label: 'Max Penalty Amount',
      required: false,
    },
  },
  [LoanProgramFieldName.PREPAY_DESCRIPTION]: {
    fieldId: LoanProgramFieldName.PREPAY_DESCRIPTION,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.PREPAY_DESCRIPTION,
    inputProps: {
      label: 'Prepayment Description',
      required: false,
    },
  },
  [LoanProgramFieldName.ANNUAL_FEE]: {
    fieldId: LoanProgramFieldName.ANNUAL_FEE,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.ANNUAL_FEE,
    inputProps: {
      type: FieldInputType.CURRENCY,
      label: 'Annual Fee',
      required: false,
      minValue: 0,
    },
  },
  [LoanProgramFieldName.MONTHLY_FEE]: {
    fieldId: LoanProgramFieldName.MONTHLY_FEE,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.MONTHLY_FEE,
    inputProps: {
      type: FieldInputType.CURRENCY,
      label: 'Monthly Fee',
      required: false,
    },
  },
  [LoanProgramFieldName.INIT_DRAW_PERIOD]: {
    fieldId: LoanProgramFieldName.INIT_DRAW_PERIOD,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.INIT_DRAW_PERIOD,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Initial Draw Period (months)',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_INIT_DRAW_PERIOD_INPUT,
    },
  },
  [LoanProgramFieldName.INIT_DRAW_MIN_TYPE]: {
    fieldId: LoanProgramFieldName.INIT_DRAW_MIN_TYPE,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.INIT_DRAW_MIN_TYPE,
    inputProps: {
      options: minimumInitialDrawTypeOptions,
      label: 'Min Initial Draw Type',
      required: false,
    },
  },
  [LoanProgramFieldName.INIT_DRAW_MIN_PERCENT]: {
    fieldId: LoanProgramFieldName.INIT_DRAW_MIN_PERCENT,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.INIT_DRAW_MIN_PERCENT,
    inputProps: {
      ...ratePercentInputProps,
      label: 'Min Initial Draw',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_INIT_DRAW_MIN_PERCENT_INPUT,
    },
  },
  [LoanProgramFieldName.INIT_DRAW_MIN_AMOUNT]: {
    fieldId: LoanProgramFieldName.INIT_DRAW_MIN_AMOUNT,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.INIT_DRAW_MIN_AMOUNT,
    inputProps: {
      type: FieldInputType.CURRENCY,
      label: 'Min Initial Draw Amount',
      required: false,
    },
  },
  [LoanProgramFieldName.INIT_DRAW_AMORT_TYPE]: {
    fieldId: LoanProgramFieldName.INIT_DRAW_AMORT_TYPE,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.INIT_DRAW_AMORT_TYPE,
    inputProps: {
      options: amortizationTypeOptions,
      label: 'Draw Period Amort Type',
      required: false,
    },
  },
  [LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE]: {
    fieldId: LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE,
    inputProps: {
      options: rateTypeOptions,
      label: 'Draw Period Rate Type',
      required: false,
    },
  },
  [LoanProgramFieldName.REPAY_PERIOD]: {
    fieldId: LoanProgramFieldName.REPAY_PERIOD,
    type: FieldType.INPUT,
    nameInModel: LoanProgramFieldName.REPAY_PERIOD,
    inputProps: {
      type: FieldInputType.INT,
      label: 'Repayment Period (months)',
      required: false,
      decimalScale: 0,
      dataTestId: PropertyAndLoanTestIds.LOAN_PROGRAM_FORM_REPAY_PERIOD_INPUT,
    },
  },
  [LoanProgramFieldName.REPAY_TYPE]: {
    fieldId: LoanProgramFieldName.REPAY_TYPE,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.REPAY_TYPE,
    inputProps: {
      options: rateTypeOptions,
      label: 'Repayment Type',
      required: false,
    },
  },
  [LoanProgramFieldName.CHAR_ARM]: {
    fieldId: LoanProgramFieldName.CHAR_ARM,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_ARM,
    inputProps: { label: 'ARM' },
  },
  [LoanProgramFieldName.CHAR_ASSUMABLE]: {
    fieldId: LoanProgramFieldName.CHAR_ASSUMABLE,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_ASSUMABLE,
    inputProps: { label: 'Assumable' },
  },
  [LoanProgramFieldName.CHAR_CONFORMING]: {
    fieldId: LoanProgramFieldName.CHAR_CONFORMING,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_CONFORMING,
    inputProps: { label: 'Conforming' },
  },
  [LoanProgramFieldName.CHAR_COUNSELING_REQUIRED]: {
    fieldId: LoanProgramFieldName.CHAR_COUNSELING_REQUIRED,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_COUNSELING_REQUIRED,
    inputProps: { label: 'Counseling Required' },
  },
  [LoanProgramFieldName.CHAR_INTEREST_ONLY]: {
    fieldId: LoanProgramFieldName.CHAR_INTEREST_ONLY,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_INTEREST_ONLY,
    inputProps: { label: 'Interest Only' },
  },
  [LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY]: {
    fieldId: LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY,
    inputProps: { label: 'Has Prepayment Penalty' },
  },
  [LoanProgramFieldName.CHAR_RUN_AUS]: {
    fieldId: LoanProgramFieldName.CHAR_RUN_AUS,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_RUN_AUS,
    inputProps: { label: 'Run AUS' },
  },
  [LoanProgramFieldName.CHAR_AFFORDABLE_LENDING]: {
    fieldId: LoanProgramFieldName.CHAR_AFFORDABLE_LENDING,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_AFFORDABLE_LENDING,
    inputProps: { label: 'Affordable Lending' },
  },
  [LoanProgramFieldName.CHAR_TEXAS_CASH_OUT]: {
    fieldId: LoanProgramFieldName.CHAR_TEXAS_CASH_OUT,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_TEXAS_CASH_OUT,
    inputProps: { label: 'Texas Cash Out' },
  },
  [LoanProgramFieldName.CHAR_DEMAND_FEATURE]: {
    fieldId: LoanProgramFieldName.CHAR_DEMAND_FEATURE,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_DEMAND_FEATURE,
    inputProps: { label: 'Demand Feature' },
  },
  [LoanProgramFieldName.CHAR_NEG_AMORT]: {
    fieldId: LoanProgramFieldName.CHAR_NEG_AMORT,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_NEG_AMORT,
    inputProps: { label: 'Negative Amortization' },
  },
  [LoanProgramFieldName.CHAR_BALLOON_PAYMENT]: {
    fieldId: LoanProgramFieldName.CHAR_BALLOON_PAYMENT,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_BALLOON_PAYMENT,
    inputProps: { label: 'Balloon Payment' },
  },
  [LoanProgramFieldName.CHAR_BRIDGE_LOAN]: {
    fieldId: LoanProgramFieldName.CHAR_BRIDGE_LOAN,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_BRIDGE_LOAN,
    inputProps: { label: 'Bridge Loan' },
  },
  [LoanProgramFieldName.CHAR_CONSTRUCTION_ONLY]: {
    fieldId: LoanProgramFieldName.CHAR_CONSTRUCTION_ONLY,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_CONSTRUCTION_ONLY,
    inputProps: { label: 'Construction Only' },
  },
  [LoanProgramFieldName.CHAR_MORE_THAN_2_PAYMENTS]: {
    fieldId: LoanProgramFieldName.CHAR_MORE_THAN_2_PAYMENTS,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_MORE_THAN_2_PAYMENTS,
    inputProps: { label: 'More Than 2 Payments Collected' },
  },
  [LoanProgramFieldName.CHAR_INTEREST_INCREASED]: {
    fieldId: LoanProgramFieldName.CHAR_INTEREST_INCREASED,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_INTEREST_INCREASED,
    inputProps: { label: 'Interest Increased After Default' },
  },
  [LoanProgramFieldName.CHAR_DEMAND_CLAUSE]: {
    fieldId: LoanProgramFieldName.CHAR_DEMAND_CLAUSE,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_DEMAND_CLAUSE,
    inputProps: { label: 'Demand Clause' },
  },
  [LoanProgramFieldName.CHAR_LENDER_PAID_MI]: {
    fieldId: LoanProgramFieldName.CHAR_LENDER_PAID_MI,
    type: FieldType.CHECKBOX,
    nameInModel: LoanProgramFieldName.CHAR_LENDER_PAID_MI,
    inputProps: { label: 'Lender Paid MI' },
  },
  [LoanProgramFieldName.ASSUMABLE_DETAILS]: {
    fieldId: LoanProgramFieldName.ASSUMABLE_DETAILS,
    type: FieldType.SELECT,
    nameInModel: LoanProgramFieldName.ASSUMABLE_DETAILS,
    inputProps: {
      options: assumableDetailsOptions,
      withSortedOptions: false,
      label: 'Assumable Details',
      required: false,
    },
  },
};

const INDEX_FIELD_IDS = [
  LoanProgramFieldName.INDEX_TYPE_ID,
  LoanProgramFieldName.INDEX_MARGIN,
  LoanProgramFieldName.INDEX_RATE,
  LoanProgramFieldName.INDEX_PERIOD_DAYS,
];

const REQUIRED_INDEX_FIELD_IDS = [
  LoanProgramFieldName.INDEX_TYPE_ID,
  LoanProgramFieldName.INDEX_PERIOD_DAYS,
];

export const checkIsLoanProgramIndexRequired = ({ form }) =>
  (!!form?.[LoanProgramFieldName.CHAR_ARM] ||
    form?.[LoanProgramFieldName.PROGRAM_LOAN_TYPE]?.value === LoanType.HELOC) &&
  INDEX_FIELD_IDS.some(fieldId => {
    const value = form?.[fieldId];

    return _.isPlainObject(value) ? !_.isNil(value.value) : !!String(value ?? '').trim();
  });

const getIndexField = ({ fieldId, indexTypesOptions, isIndexRequired }) => ({
  ...loanProgramFields[fieldId],
  inputProps: {
    ...loanProgramFields[fieldId].inputProps,
    ...(fieldId === LoanProgramFieldName.INDEX_TYPE_ID && { options: indexTypesOptions }),
    required: isIndexRequired && REQUIRED_INDEX_FIELD_IDS.includes(fieldId),
  },
});

export const getLoanProgramConfig = compose(
  fillConfigFieldsWithKeys,
  ({ form, indexTypesOptions, isIndexRequired }) => {
    const isArm = !!form[LoanProgramFieldName.CHAR_ARM];
    const isHasPrepayPenalty = !!form[LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY];
    const isInterestOnly = !!form[LoanProgramFieldName.CHAR_INTEREST_ONLY];
    const isBalloonPayment = !!form[LoanProgramFieldName.CHAR_BALLOON_PAYMENT];
    const isAssumable = !!form[LoanProgramFieldName.CHAR_ASSUMABLE];
    const programLoanType = form[LoanProgramFieldName.PROGRAM_LOAN_TYPE]?.value;
    const isHeloc = programLoanType === LoanType.HELOC;
    const showIndex = isArm || isHeloc;
    const initDrawMinType = form[LoanProgramFieldName.INIT_DRAW_MIN_TYPE]?.value;

    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            loanProgramFields[LoanProgramFieldName.PROGRAM_CODE],
            loanProgramFields[LoanProgramFieldName.PROGRAM_LOAN_TYPE],
            loanProgramFields[LoanProgramFieldName.LOAN_TERM],
            loanProgramFields[LoanProgramFieldName.AMORT_TERM],
            loanProgramFields[LoanProgramFieldName.AMORTIZATION_TYPE],
            ...getConditionalArray(isInterestOnly, [
              loanProgramFields[LoanProgramFieldName.INTEREST_ONLY_PERIOD],
            ]),
            ...getConditionalArray(isBalloonPayment, [
              loanProgramFields[LoanProgramFieldName.BALLOON_TERM_MONTHS],
            ]),
          ],
          key: 'lp-core-terms',
          groupTitle: { label: 'Core Terms' },
        },
        ...getConditionalArray(isArm, [
          {
            type: FieldType.GROUP,
            fields: [
              loanProgramFields[LoanProgramFieldName.ARM_INITIAL_CAP],
              loanProgramFields[LoanProgramFieldName.ARM_ADJUSTMENT_CAP],
              loanProgramFields[LoanProgramFieldName.ARM_SUBSEQUENT_CAP],
              loanProgramFields[LoanProgramFieldName.ARM_RATE_ADJ_PERIOD],
              loanProgramFields[LoanProgramFieldName.ARM_SUB_RATE_ADJ_PERIOD],
              loanProgramFields[LoanProgramFieldName.ARM_FLOOR_RATE],
              loanProgramFields[LoanProgramFieldName.ARM_CEILING_RATE],
              loanProgramFields[LoanProgramFieldName.ARM_ADJ_CHANGE_INDEX_RATE],
            ],
            key: 'lp-arm-terms',
            groupTitle: { label: 'ARM Terms' },
          },
        ]),
        ...getConditionalArray(showIndex, [
          {
            type: FieldType.GROUP,
            fields: INDEX_FIELD_IDS.map(fieldId =>
              getIndexField({ fieldId, indexTypesOptions, isIndexRequired }),
            ),
            key: 'lp-index',
            groupTitle: { label: 'Index' },
          },
        ]),
        ...getConditionalArray(isHasPrepayPenalty, [
          {
            type: FieldType.GROUP,
            fields: [
              loanProgramFields[LoanProgramFieldName.PREPAY_FEATURE_TYPE],
              loanProgramFields[LoanProgramFieldName.PREPAY_MONTHS_IN_AFFECT],
              loanProgramFields[LoanProgramFieldName.PREPAY_CHARGE_TERM_MONTHS],
              loanProgramFields[LoanProgramFieldName.PREPAY_CHARGE_RATE],
              loanProgramFields[LoanProgramFieldName.PREPAY_MAX_AMOUNT],
              loanProgramFields[LoanProgramFieldName.PREPAY_DESCRIPTION],
            ],
            key: 'lp-prepayment',
            groupTitle: { label: 'Prepayment Penalty' },
          },
        ]),
        ...getConditionalArray(isHeloc, () => [
          {
            type: FieldType.GROUP,
            fields: [
              loanProgramFields[LoanProgramFieldName.ANNUAL_FEE],
              loanProgramFields[LoanProgramFieldName.MONTHLY_FEE],
              loanProgramFields[LoanProgramFieldName.INIT_DRAW_PERIOD],
              loanProgramFields[LoanProgramFieldName.INIT_DRAW_MIN_TYPE],
              ...getConditionalArray(
                initDrawMinType === MinimumInitialDrawType.LINE_LIMIT_PERCENTAGE,
                [loanProgramFields[LoanProgramFieldName.INIT_DRAW_MIN_PERCENT]],
              ),
              ...getConditionalArray(initDrawMinType === MinimumInitialDrawType.CURRENCY_AMOUNT, [
                loanProgramFields[LoanProgramFieldName.INIT_DRAW_MIN_AMOUNT],
              ]),
              loanProgramFields[LoanProgramFieldName.INIT_DRAW_AMORT_TYPE],
              loanProgramFields[LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE],
              loanProgramFields[LoanProgramFieldName.REPAY_PERIOD],
              loanProgramFields[LoanProgramFieldName.REPAY_TYPE],
            ],
            key: 'lp-heloc',
            groupTitle: { label: 'HELOC' },
          },
        ]),
        {
          type: FieldType.GROUP,
          fields: [
            loanProgramFields[LoanProgramFieldName.CHAR_ARM],
            loanProgramFields[LoanProgramFieldName.CHAR_ASSUMABLE],
            loanProgramFields[LoanProgramFieldName.CHAR_CONFORMING],
            loanProgramFields[LoanProgramFieldName.CHAR_COUNSELING_REQUIRED],
            loanProgramFields[LoanProgramFieldName.CHAR_INTEREST_ONLY],
            loanProgramFields[LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY],
            loanProgramFields[LoanProgramFieldName.CHAR_RUN_AUS],
            loanProgramFields[LoanProgramFieldName.CHAR_AFFORDABLE_LENDING],
            loanProgramFields[LoanProgramFieldName.CHAR_TEXAS_CASH_OUT],
            loanProgramFields[LoanProgramFieldName.CHAR_DEMAND_FEATURE],
            loanProgramFields[LoanProgramFieldName.CHAR_NEG_AMORT],
            loanProgramFields[LoanProgramFieldName.CHAR_BALLOON_PAYMENT],
            loanProgramFields[LoanProgramFieldName.CHAR_BRIDGE_LOAN],
            loanProgramFields[LoanProgramFieldName.CHAR_CONSTRUCTION_ONLY],
            loanProgramFields[LoanProgramFieldName.CHAR_MORE_THAN_2_PAYMENTS],
            loanProgramFields[LoanProgramFieldName.CHAR_INTEREST_INCREASED],
            loanProgramFields[LoanProgramFieldName.CHAR_DEMAND_CLAUSE],
            loanProgramFields[LoanProgramFieldName.CHAR_LENDER_PAID_MI],
          ],
          key: 'lp-characteristics',
          groupTitle: { label: 'Loan Characteristics' },
        },
        ...getConditionalArray(isAssumable, [
          {
            type: FieldType.GROUP,
            fields: [loanProgramFields[LoanProgramFieldName.ASSUMABLE_DETAILS]],
            key: 'lp-assumable',
            groupTitle: { label: 'Assumable Details' },
          },
        ]),
      ],
    };
  },
);

export const loanProgramFieldNames = Object.keys(loanProgramFields);

export const getLoanProgramEntityConfig = compose(
  fillConfigFieldsWithKeys,
  ({ indexTypesOptions, isIndexRequired }) => ({
    groups: [
      {
        type: FieldType.GROUP,
        fields: Object.keys(loanProgramFields).map(fieldId =>
          INDEX_FIELD_IDS.includes(fieldId)
            ? getIndexField({ fieldId, indexTypesOptions, isIndexRequired })
            : loanProgramFields[fieldId],
        ),
        key: 'lp-entity-fields',
      },
    ],
  }),
);
